import { SupportedChainId } from 'config/chains'
import type { ComponentType } from 'react'
import { WrappedTokenInfo } from 'state/lists/wrappedTokenInfo'
import { filters } from 'utils/tokenFilters'

export const CATEGORY_KEYS = {
  MAIN: 'Main',
  LP_TOKENS: 'LP Tokens',
  RWA: 'RWA',
  USD: 'USD',
  MAJORS: 'Majors',
  OTHERS: 'Others',
} as const

export type CategoryKey = (typeof CATEGORY_KEYS)[keyof typeof CATEGORY_KEYS]

export type Category = {
  name: CategoryKey
  logo?: ComponentType
  filter?: (token: WrappedTokenInfo) => boolean
}

const DEFAULT_CATEGORY_KEYS: readonly CategoryKey[] = [
  CATEGORY_KEYS.MAIN,
  CATEGORY_KEYS.LP_TOKENS,
  CATEGORY_KEYS.OTHERS,
]

const CHAIN_CATEGORY_KEYS: Partial<Record<number, readonly CategoryKey[]>> = {
  [SupportedChainId.ROBINHOOD]: [CATEGORY_KEYS.RWA, CATEGORY_KEYS.USD, CATEGORY_KEYS.MAJORS, CATEGORY_KEYS.OTHERS],
}

// The native currency carries no list tags, so its tab is set per chain.
const CHAIN_NATIVE_CATEGORY: Partial<Record<number, CategoryKey>> = {
  [SupportedChainId.ROBINHOOD]: CATEGORY_KEYS.MAJORS,
}

/** The category tabs a chain offers, in display order. */
export function categoryKeysForChain(chainId: number | undefined): readonly CategoryKey[] {
  return (chainId !== undefined && CHAIN_CATEGORY_KEYS[chainId]) || DEFAULT_CATEGORY_KEYS
}

/** The category tab the chain's native currency is listed under. */
export function nativeCategoryForChain(chainId: number | undefined): CategoryKey {
  return (chainId !== undefined && CHAIN_NATIVE_CATEGORY[chainId]) || CATEGORY_KEYS.MAIN
}

// Others is whatever the chain's other categories leave out, so every token lands in some tab.
const isUncategorized = (token: WrappedTokenInfo) =>
  !categoryKeysForChain(token.chainId).some(
    (key) => key !== CATEGORY_KEYS.OTHERS && CATEGORIES_MAP[key].filter?.(token)
  )

export const CATEGORIES_MAP: Record<CategoryKey, Category> = {
  [CATEGORY_KEYS.MAIN]: { name: CATEGORY_KEYS.MAIN, filter: filters.main },
  [CATEGORY_KEYS.LP_TOKENS]: { name: CATEGORY_KEYS.LP_TOKENS, filter: filters.lp },
  [CATEGORY_KEYS.RWA]: { name: CATEGORY_KEYS.RWA, filter: filters.rwa },
  [CATEGORY_KEYS.USD]: { name: CATEGORY_KEYS.USD, filter: filters.usd },
  [CATEGORY_KEYS.MAJORS]: { name: CATEGORY_KEYS.MAJORS, filter: filters.majors },
  [CATEGORY_KEYS.OTHERS]: { name: CATEGORY_KEYS.OTHERS, filter: isUncategorized },
}

export function categoriesForChain(chainId: number | undefined): Category[] {
  return categoryKeysForChain(chainId).map((key) => CATEGORIES_MAP[key])
}
